// The exams behind the Namat scale (owner request 2026-10-07): which exam
// assessNamat used, what each of its three outcomes (BMI, blood pressure,
// HbA1c) measured and how many points it gave, and how the total splits
// between the questionnaire and the exam. Nothing here scores anything: the
// points, the missing and the excluded outcomes are read from the assessment,
// the exam is picked exactly as assessNamat picks it, and its values and
// levels come from readIndicators. Identical in web and mobile
// (src/lib/assessment/scaleBreakdown.ts); change both.
//
// An expired result is broken down the same way: assessNamat scores it with
// the exam in the window before `now`, not before the date it was filled, so
// that is the exam that explains the greyed percentage shown for it. Pass the
// same `now` the assessment was made with.

import {
  OUTCOMES,
  VALIDITY_DAYS_DEFAULT,
  ageInMonths,
  readIndicators,
  type ExamInput,
  type ExamValues,
  type IndicatorReading,
  type NamatAssessment,
  type Outcome,
  type PersonInput,
} from './namatAssessment';

const DAY_MS = 86_400_000;

/**
 * The exam assessNamat scores: the newest completed one dated within the
 * validity window before `now` (same filter and order as assessNamat).
 */
export function pickScaleExam<T extends ExamInput>(exams: T[], now: Date, validityDays?: number): T | null {
  const windowStart = now.getTime() - (validityDays ?? VALIDITY_DAYS_DEFAULT) * DAY_MS;
  return (
    exams
      .filter((e) => e.status === 'completed' && e.date.getTime() >= windowStart && e.date.getTime() <= now.getTime())
      .sort((a, b) => b.date.getTime() - a.date.getTime())[0] ?? null
  );
}

/**
 * scored: counted in the scale. excluded: does not apply at this age (blood
 * pressure under 13). not_measured: no value in the exam, or no valid exam.
 * not_scored: a value was recorded but gives no points (a BMI under age 2, or
 * only one of the two blood pressure values).
 */
export type OutcomeState = 'scored' | 'excluded' | 'not_measured' | 'not_scored';

export interface OutcomeRow {
  outcome: Outcome;
  state: OutcomeState;
  /** The measured value, unit and level; null when nothing was recorded. */
  reading: IndicatorReading | null;
  /** Points given, or null when not counted. */
  points: number | null;
  max: number;
}

export interface PartScore {
  score: number;
  max: number;
}

export interface ScaleBreakdown {
  /** The exam the scale used; null when there is no valid exam. */
  exam: { date: Date } | null;
  outcomes: OutcomeRow[];
  questionnaire: PartScore;
  /** The exam's outcomes; 0/0 when none is counted. */
  examPart: PartScore;
  total: PartScore & { percent: number };
}

/** The breakdown of an assessment's scale; null when it has no scale (questionnaire not filled). */
export function buildScaleBreakdown(input: {
  assessment: NamatAssessment;
  exams: ExamInput[];
  now: Date;
  validityDays?: number;
  person?: PersonInput;
}): ScaleBreakdown | null {
  const scale = input.assessment.scale;
  if (!scale) return null;
  const exam = pickScaleExam(input.exams, input.now, input.validityDays);
  const birthDate = input.person?.birthDate ?? null;
  const readings = exam
    ? readIndicators(exam as ExamValues, {
        ageMonths: birthDate ? ageInMonths(birthDate, exam.date) : null,
        sex: input.person?.sex,
      })
    : [];

  const outcomes: OutcomeRow[] = OUTCOMES.map((outcome) => {
    const reading = readings.find((r) => r.key === outcome) ?? null;
    const item = scale.items.find((i) => i.kind === 'outcome' && i.key === outcome);
    if (item) return { outcome, state: 'scored', reading, points: item.score, max: item.max };
    const state: OutcomeState = scale.excluded.includes(outcome) ? 'excluded' : reading ? 'not_scored' : 'not_measured';
    return { outcome, state, reading, points: null, max: 3 };
  });

  const part = (kind: 'question' | 'outcome'): PartScore => {
    const items = scale.items.filter((i) => i.kind === kind);
    return { score: items.reduce((s, i) => s + i.score, 0), max: items.reduce((s, i) => s + i.max, 0) };
  };

  return {
    exam: exam ? { date: exam.date } : null,
    outcomes,
    questionnaire: part('question'),
    examPart: part('outcome'),
    total: { score: scale.score, max: scale.max, percent: scale.percent },
  };
}
